import { motion, useReducedMotion } from 'framer-motion';

/** Shared easing from Landing · Scroll Beat frames in the .pen */
export const LANDING_EASE = [0.22, 1, 0.36, 1];

const VIEWPORT = { once: true, amount: 0.35 };

/**
 * Scroll / mount reveal used by the Landing storytelling sections.
 * Motion intent lives on .pen `context` fields (fade-up, stagger, slide-in).
 */
export function ScrollReveal({
  children,
  className,
  as = 'div',
  delay = 0,
  y = 24,
  x = 0,
  scale,
  duration = 0.55,
  onMount = false,
  ...rest
}) {
  const reduceMotion = useReducedMotion();
  const Comp = motion[as] || motion.div;

  if (reduceMotion) {
    const Static = as === 'div' ? 'div' : as;
    return (
      <Static className={className} {...rest}>
        {children}
      </Static>
    );
  }

  const initial = {
    opacity: 0,
    ...(y ? { y } : {}),
    ...(x ? { x } : {}),
    ...(scale != null ? { scale } : {}),
  };

  return (
    <Comp
      className={className}
      initial={initial}
      {...(onMount
        ? { animate: { opacity: 1, y: 0, x: 0, scale: 1 } }
        : { whileInView: { opacity: 1, y: 0, x: 0, scale: 1 }, viewport: VIEWPORT })}
      transition={{ duration, delay, ease: LANDING_EASE }}
      {...rest}
    >
      {children}
    </Comp>
  );
}

export function StaggerGroup({
  children,
  className,
  stagger = 0.08,
  delayChildren = 0,
  onMount = false,
  ...rest
}) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return (
      <div className={className} {...rest}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      className={className}
      initial="hidden"
      {...(onMount
        ? { animate: 'show' }
        : { whileInView: 'show', viewport: VIEWPORT })}
      variants={{
        hidden: {},
        show: {
          transition: { staggerChildren: stagger, delayChildren },
        },
      }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, className, y = 24, x = 0, scale, ...rest }) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return (
      <div className={className} {...rest}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      className={className}
      variants={{
        hidden: {
          opacity: 0,
          ...(y ? { y } : {}),
          ...(x ? { x } : {}),
          ...(scale != null ? { scale } : {}),
        },
        show: {
          opacity: 1,
          y: 0,
          x: 0,
          scale: 1,
          transition: { duration: 0.55, ease: LANDING_EASE },
        },
      }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}
